import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { VTimeSeriesChart } from '@/component';
import {
  DATE_RANGE_TAB_LIST_PRESET,
  type PeriodUnitListPreset,
  RateReview,
  RestartAlt,
  Schedule,
  VFlex,
  VPeriodSelector,
  VStack,
} from '@/package';
import { useLangStore } from '@/store';
import type { WorktimeStatistic } from '@/type';
import { makeAvgTime, workTimeToString } from '@/util';

import { StatisticSummaryCard } from '../StatisticSummaryCard';
import type { PeriodUnit } from '../type';
import { WORKTIME_PERIOD_UNIT } from './constant';

export const WorkFlowPerformance = ({
  dateRange,
  periodUnit,
  worktimeGroupByDate,
  onChangeDateRange,
  onChangePeriodUnit,
}: {
  dateRange: [Date, Date];
  periodUnit: PeriodUnit;
  worktimeGroupByDate: WorktimeStatistic[];
  onChangeDateRange?: (dateRange: [Date, Date]) => void;
  onChangePeriodUnit?: (periodUnit: PeriodUnit) => void;
}) => {
  const { t } = useTranslation();
  const language = useLangStore(state => state.language);
  const dateRangeTabList = useMemo(
    () => DATE_RANGE_TAB_LIST_PRESET[language as LanguageCode],
    [language],
  );
  const periodUnitList = useMemo(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    () =>
      [
        WORKTIME_PERIOD_UNIT.DAILY,
        WORKTIME_PERIOD_UNIT.WEEKLY,
        WORKTIME_PERIOD_UNIT.MONTHLY,
      ].map(
        v =>
          ({
            ...v,
            label: t(v.label),
          }) as PeriodUnitListPreset[number],
      ),
    [language],
  );

  const chartData = useMemo(() => {
    if (!worktimeGroupByDate) {
      return null;
    }

    return {
      'Avg Work Time': worktimeGroupByDate.map(v => ({
        timestamp: v.date,
        value: v.workTime / v.workFrameCount,
      })),
      'Avg Review Time': worktimeGroupByDate.map(v => ({
        timestamp: v.date,
        value: v.reviewTime / v.reviewFrameCount,
      })),
      'Avg Rework Time': worktimeGroupByDate.map(v => ({
        timestamp: v.date,
        value: v.reworkTime / v.reworkFrameCount,
      })),
    };
  }, [worktimeGroupByDate]);

  const avgTime = useMemo(() => {
    if (!chartData) {
      return {
        work: '0m 0s',
        review: '0m 0s',
        rework: '0m 0s',
      };
    }

    return makeAvgTime({
      workTime: chartData['Avg Work Time'][0]?.value ?? 0,
      workFrameCount: 1,
      reviewTime: chartData['Avg Review Time'][0]?.value ?? 0,
      reviewFrameCount: 1,
      reworkTime: chartData['Avg Rework Time'][0]?.value ?? 0,
      reworkFrameCount: 1,
      date: '',
    });
  }, [chartData]);

  return (
    <VStack gap={1} className="w-full">
      <VFlex wrap="wrap" justify="between" gap={0.5}>
        <div className="w-fit text-headline-md font-bold text-blue-300">
          Workflow Performance
        </div>

        <VPeriodSelector
          dateRangeTabList={dateRangeTabList}
          dateRangeValue={dateRange}
          enableAggregationGap={false}
          periodUnitList={periodUnitList}
          periodUnitValue={periodUnit}
          onDateRangeChange={dateRange => {
            const start = dayjs(dateRange[0]).utc().startOf('day').toDate();
            const end = dayjs(dateRange[1]).utc().endOf('day').toDate();

            onChangeDateRange?.([start, end]);
          }}
          onPeriodUnitChange={newPeriodUnit =>
            onChangePeriodUnit?.(newPeriodUnit as PeriodUnit)
          }
        />
      </VFlex>

      <div>
        {chartData && (
          <VTimeSeriesChart
            autoFillData
            chartType="line"
            data={chartData}
            dateRange={dateRange}
            datePeriodUnit="day"
            seriesOption={{
              'Avg Work Time': { color: '#43DD84' },
              'Avg Review Time': { color: '#EF96FF' },
              'Avg Rework Time': { color: '#22A89F' },
            }}
            echartsProps={{
              style: { width: '100%', height: '382px' },
              tooltip: {
                trigger: 'axis',
                valueFormatter(value) {
                  if (!value || typeof value !== 'number') return '0m 0s';

                  return workTimeToString(value);
                },
              },
              grid: {
                left: '40px',
                right: '40px',
                bottom: '48px',
                top: '26px',
                containLabel: true,
              },
              xAxis: {
                axisLabel: {
                  rotate: 0,
                  align: 'center',
                  margin: 20,
                  color: '#000',
                  formatter(value: string) {
                    return dayjs(value).format('MM-DD');
                  },
                },
              },
              yAxis: {
                axisLabel: {
                  color: '#000',
                  formatter: (value: string) => `${value}m`,
                },
              },
            }}
          />
        )}
      </div>

      {chartData && (
        <VFlex justify="center" items="center" gap={1}>
          <StatisticSummaryCard
            Icon={Schedule}
            className="w-[231px] flex-none py-4.5"
            content={() => avgTime.work}
            title="Avg Work Time"
          />

          <StatisticSummaryCard
            Icon={RateReview}
            className="w-[231px] flex-none py-4.5"
            content={() => avgTime.review}
            title="Avg Review Time"
          />

          <StatisticSummaryCard
            Icon={RestartAlt}
            className="w-[231px] flex-none py-4.5"
            content={() => avgTime.rework}
            title="Avg Rework Time"
          />
        </VFlex>
      )}
    </VStack>
  );
};

WorkFlowPerformance.displayName = 'WorkFlowPerformance';
